import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import { useGetOrgQuery, useSetOrgStatusMutation, useDeleteOrgMutation } from '@proaxiv/server-api'
import type { GetOrganizationResponse, ApiError } from '@proaxiv/server-api'
import { Button } from './Button'

// ── Icons ─────────────────────────────────────────────────────────────────────

const PowerIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
)

const DangerIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
)

const XIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const CheckIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const InfoIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
)

// ── ConfirmModal ──────────────────────────────────────────────────────────────

type ConfirmVariant = 'warning' | 'danger'

interface ConfirmModalProps {
  title: string
  description: string
  confirmLabel: string
  variant: ConfirmVariant
  isLoading: boolean
  onConfirm: () => void
  onClose: () => void
}

function ConfirmModal({
  title,
  description,
  confirmLabel,
  variant,
  isLoading,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const { t } = useTranslation()

  const confirmStyle =
    variant === 'danger'
      ? { background: 'linear-gradient(135deg, #FF5B37 0%, #f2a529 100%)' }
      : undefined

  return (
    <Modal backdrop="light" onBackdropClick={isLoading ? undefined : onClose}>
      <div className="border-pax-purple/50 flex w-full max-w-sm flex-col gap-5 rounded-2xl border bg-white p-6 shadow-xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="text-pax-gray hover:text-pax-black absolute top-4 right-4 cursor-pointer transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Close"
        >
          <XIcon />
        </button>

        <div className="flex flex-col gap-1 pr-6">
          <div className="flex items-center gap-2">
            <div
              className={[
                'flex h-7 w-7 items-center justify-center rounded-lg',
                variant === 'danger'
                  ? 'bg-pax-red/10 text-pax-red'
                  : 'bg-pax-orange/10 text-pax-orange',
              ].join(' ')}
            >
              <DangerIcon size={13} />
            </div>
            <h2 className="text-pax-black text-base font-bold">{title}</h2>
          </div>
          <p className="text-pax-gray text-xs">{description}</p>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isLoading}>
            {t('btn_cancel')}
          </Button>
          {isLoading ? (
            <div
              className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg"
              style={confirmStyle ?? { background: '#3B82F6' }}
            >
              <svg
                className="h-4 w-4 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-label="Loading"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                />
              </svg>
            </div>
          ) : (
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-85 focus-visible:outline-none"
              style={confirmStyle ?? { background: '#3B82F6' }}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ── OrgSecurityPanel ──────────────────────────────────────────────────────────

interface OrgSecurityPanelProps {
  orgId: string
  isOwner: boolean
}

/**
 * Security settings panel — organization status (enable/disable) and
 * permanent deletion. Shown on the `/org/security` route.
 */
export function OrgSecurityPanel({ orgId, isOwner }: OrgSecurityPanelProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: rawOrg } = useGetOrgQuery({ orgId }, { skip: !orgId })
  const org = rawOrg as GetOrganizationResponse | undefined

  const [setOrgStatus, { isLoading: isSettingStatus }] = useSetOrgStatusMutation()
  const [deleteOrg, { isLoading: isDeleting }] = useDeleteOrgMutation()

  const [confirmDisable, setConfirmDisable] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const isEnabled = org?.status === 'ENABLED' || org?.status === 'ACCEPTED'
  const isInactive = org?.status === 'INACTIVE'

  async function handleToggleStatus() {
    setActionError(null)
    const newStatus = isEnabled ? 'INACTIVE' : 'ENABLED'
    const result = await setOrgStatus({ orgId, orgStatusBody: { status: newStatus } })
    if ('error' in result) {
      const err = result.error
      let message = 'Failed to update organization status. Please try again.'
      if (err && 'data' in err && err.data && typeof err.data === 'object') {
        const apiError = err.data as ApiError
        if (apiError.message) message = apiError.message
      }
      setActionError(message)
    }
    setConfirmDisable(false)
  }

  async function handleDelete() {
    setActionError(null)
    const result = await deleteOrg({ orgId })
    if ('error' in result) {
      const err = result.error
      let message = 'Failed to delete organization. Please try again.'
      if (err && 'data' in err && err.data && typeof err.data === 'object') {
        const apiError = err.data as ApiError
        if (apiError.message) message = apiError.message
      }
      setActionError(message)
      setConfirmDelete(false)
      return
    }
    navigate('/home', { replace: true })
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Error banner */}
        {actionError && (
          <div className="bg-pax-red/8 border-pax-red/20 flex items-start gap-2 rounded-xl border px-4 py-3">
            <svg
              className="text-pax-red mt-0.5 h-3.5 w-3.5 shrink-0"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p className="text-pax-red text-xs">{actionError}</p>
          </div>
        )}

        {/* Organization Status card */}
        <div className="border-pax-purple/20 flex flex-col gap-0 overflow-hidden rounded-2xl border bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex items-center gap-2.5">
                <span className="bg-pax-light-blue text-pax-blue flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
                  <PowerIcon size={14} />
                </span>
                <h3 className="text-pax-black text-sm font-bold">
                  {t('org_security_status_title')}
                </h3>
              </div>
              <p className="text-pax-gray text-xs break-words">
                {t('org_security_status_subtitle')}
              </p>
            </div>

            {isOwner && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<PowerIcon size={13} />}
                onClick={() => (isEnabled ? setConfirmDisable(true) : void handleToggleStatus())}
                disabled={isSettingStatus}
                className="w-full justify-center sm:w-auto"
              >
                {isEnabled ? t('org_security_btn_disable') : t('org_security_btn_enable')}
              </Button>
            )}
          </div>

          <div className="border-pax-purple/15 border-t px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="text-pax-gray text-xs font-medium">
                {t('org_security_status_label')}
              </span>
              {isEnabled && (
                <span className="inline-flex items-center gap-1 rounded-md bg-green-500 px-2 py-0.5 text-xs font-semibold text-white">
                  <CheckIcon size={11} />
                  {t('org_security_status_enabled')}
                </span>
              )}
              {isInactive && (
                <span className="text-pax-gray inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-semibold">
                  {t('org_security_status_disabled')}
                </span>
              )}
              {!isEnabled && !isInactive && org?.status && (
                <span className="text-pax-gray inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-semibold capitalize">
                  {org.status.toLowerCase()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Danger Zone card */}
        <div className="relative overflow-hidden rounded-2xl border border-red-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          {/* Left accent stripe */}
          <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#FF5B37] to-[#f2a529]" />

          {/* Header */}
          <div className="bg-pax-red/[0.03] px-5 py-4 pl-6">
            <div className="flex items-center gap-2.5">
              <span className="bg-pax-red/10 text-pax-red flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
                <DangerIcon size={14} />
              </span>
              <div className="flex flex-col">
                <h3 className="text-pax-red text-sm font-bold tracking-tight">
                  {t('org_security_danger_title')}
                </h3>
                <p className="text-pax-gray text-xs">{t('org_security_danger_subtitle')}</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-red-100" />

          {/* Delete row */}
          <div className="flex flex-col gap-3 px-5 py-4 pl-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-pax-black text-sm font-semibold break-words">
                {t('org_security_delete_title', { name: org?.name ?? orgId })}
              </span>
              <p className="text-pax-gray text-xs">{t('org_security_delete_subtitle')}</p>
            </div>

            {isOwner && (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<XIcon size={12} />}
                onClick={() => setConfirmDelete(true)}
                disabled={isDeleting}
                className="w-full shrink-0 justify-center sm:w-auto"
              >
                {t('org_security_delete_btn')}
              </Button>
            )}
          </div>

          {/* Info note */}
          <div className="border-t border-red-100">
            <div className="bg-pax-light-blue/60 flex items-start gap-2 px-5 py-3 pl-6">
              <span className="text-pax-blue mt-0.5 shrink-0">
                <InfoIcon size={14} />
              </span>
              <div className="min-w-0">
                <span className="text-pax-blue text-xs font-semibold">
                  {t('org_security_delete_note_title')}
                </span>
                <p className="text-pax-gray text-xs break-words">
                  {t('org_security_delete_note_text')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disable confirmation */}
      {confirmDisable && (
        <ConfirmModal
          title={t('org_security_confirm_disable_title')}
          description={t('org_security_confirm_disable_text')}
          confirmLabel={t('org_security_btn_disable')}
          variant="warning"
          isLoading={isSettingStatus}
          onConfirm={() => void handleToggleStatus()}
          onClose={() => setConfirmDisable(false)}
        />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <ConfirmModal
          title={t('org_security_confirm_delete_title')}
          description={t('org_security_confirm_delete_text')}
          confirmLabel={t('org_security_delete_btn')}
          variant="danger"
          isLoading={isDeleting}
          onConfirm={() => void handleDelete()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </>
  )
}
